import type { RunEventRow, RunStepRow, StepStatus } from "@shared/types/runs";

/**
 * #1318: how each EARLIER attempt of a retried step ended, read from the run's own events.
 * `run.steps` carries only the latest attempt; every attempt's ending is a `STEP_*` event with
 * its `attempt`, and a retry writes `STEP_RETRY_REQUESTED` (the new attempt and the status the
 * step had). Outcome only: never counts, rows or error text (an attempt's error is per attempt
 * and reduced elsewhere).
 *
 * A retry reopens the steps after the retried one WITHOUT moving their attempt, so a reopened
 * step has no earlier attempt and reads as today. When a reopened pass of an attempt runs again,
 * the attempt's latest pass is the one told (the run log shows every pass).
 */

/** The events the page asks for, oldest first. */
export const STEP_ATTEMPT_EVENT_CODES: readonly string[] = [
  "STEP_SUCCEEDED",
  "STEP_FAILED",
  "STEP_SKIPPED",
  "STEP_CANCELLED",
  "STEP_RETRY_REQUESTED",
];

/** The most pages one read follows (200 events each); a longer log leaves its OLDEST events out. */
export const STEP_ATTEMPT_MAX_PAGES = 10;

type Ending = "succeeded" | "failed" | "skipped" | "cancelled";
const ENDINGS: readonly string[] = ["succeeded", "failed", "skipped", "cancelled"];

function isEnding(value: unknown): value is Ending {
  return typeof value === "string" && ENDINGS.includes(value);
}

/** step key -> attempt number -> how that attempt ended. */
export type StepAttemptHistory = ReadonlyMap<string, ReadonlyMap<number, Ending>>;

export function stepAttemptKey(row: Pick<RunStepRow, "dataflowId" | "step">): string {
  return `${row.dataflowId ?? ""}::${row.step}`;
}

function attemptOf(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 ? value : null;
}

type Table = Map<string, Map<number, Ending>>;

function put(into: Table, key: string, attempt: number, ending: Ending): void {
  const byAttempt = into.get(key) ?? new Map<number, Ending>();
  byAttempt.set(attempt, ending);
  into.set(key, byAttempt);
}

/** Folds events (oldest first, the order passes happened in) into each step's attempt endings. */
export function foldStepAttempts(events: readonly RunEventRow[]): StepAttemptHistory {
  const endings: Table = new Map();
  const fromRetry: Table = new Map();
  for (const event of events) {
    const payload = (event.payload ?? {}) as Record<string, unknown>;
    if (typeof payload.step !== "string") continue;
    const key = `${event.dataflowId ?? ""}::${payload.step}`;
    const attempt = attemptOf(payload.attempt);
    if (attempt === null) continue;
    if (event.event === "STEP_RETRY_REQUESTED") {
      // `attempt` is the NEW one, so the previous status is attempt - 1's ending.
      if (attempt > 1 && isEnding(payload.previousStatus)) {
        put(fromRetry, key, attempt - 1, payload.previousStatus);
      }
    } else if (event.event.startsWith("STEP_")) {
      const word = event.event.slice("STEP_".length).toLowerCase();
      if (isEnding(word)) put(endings, key, attempt, word);
    }
  }
  // The retry request wins: it holds the step's status at the moment of the retry, read under
  // the row's lock. A cancel writes no step event and a reopen keeps the attempt, so an older
  // ending event can describe an earlier pass of the same attempt. The ending events fill an
  // attempt with no retry request after it.
  for (const [key, byAttempt] of fromRetry) {
    for (const [attempt, ending] of byAttempt) put(endings, key, attempt, ending);
  }
  return endings;
}

const WORDS: Record<StepStatus, string> = {
  succeeded: "succeeded",
  failed: "failed",
  skipped: "was skipped",
  cancelled: "was cancelled",
  running: "is running",
  pending: "is waiting",
};

/**
 * "Attempt 1 failed, attempt 2 succeeded." for a retried step, or null for a step never retried
 * or one whose earlier attempts cannot all be told (say nothing rather than guess).
 */
export function attemptsSentence(
  history: StepAttemptHistory,
  row: RunStepRow,
  runTerminal = false,
): string | null {
  if (row.attempt <= 1) return null;
  const known = history.get(stepAttemptKey(row));
  const parts: string[] = [];
  for (let attempt = 1; attempt < row.attempt; attempt += 1) {
    const ending = known?.get(attempt);
    if (!ending) return null;
    parts.push(`attempt ${attempt} ${WORDS[ending]}`);
  }
  // A pending step in a finished run will never start (the badge says "Not run"), so its
  // current attempt is left out rather than called "waiting".
  if (!(runTerminal && row.status === "pending")) {
    parts.push(`attempt ${row.attempt} ${WORDS[row.status]}`);
  }
  const sentence = parts.join(", ");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}
